import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { User } from '../types/user';
import { userService } from '../services/userService';
import { useTickets } from '../hooks/useTickets';
import { Ticket } from '../types/ticket';
import { loadDateRange, saveDateRange } from '../utils/dateRangePersistence';
import {
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { CalendarIcon } from '@heroicons/react/24/solid';
import TicketList from '../components/tickets/TicketList';
import TicketTable from '../components/tickets/TicketTable';
import TicketKanbanBoard from '../components/tickets/TicketKanbanBoard';
import Pagination from '../components/common/Pagination';
import DateRangePicker from '../components/layout/DateRangePicker';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import { TICKET_STATUSES } from '../constants/ticketConstants';

const ITEMS_PER_PAGE = 12;

export default function AgentTicketsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(loadDateRange(`user-tickets-${userId}-date-range`));
  const [activeTab, setActiveTab] = useState<'requested' | 'assigned'>('requested');
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'table'>(() => {
    const savedMode = localStorage.getItem(`userTicketsViewMode-${userId}`);
    return (savedMode === 'list' || savedMode === 'kanban' || savedMode === 'table') ? savedMode : 'kanban';
  });

  // Separate filter states for requested and assigned tickets
  const [requestedCurrentPage, setRequestedCurrentPage] = useState(1);
  const [requestedSelectedStatus, setRequestedSelectedStatus] = useState('');
  const [requestedSortBy, setRequestedSortBy] = useState('createdAt');
  const [requestedSortOrder, setRequestedSortOrder] = useState<'asc' | 'desc'>('desc');

  const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
  const [assignedSelectedStatus, setAssignedSelectedStatus] = useState('');
  const [assignedSortBy, setAssignedSortBy] = useState('createdAt');
  const [assignedSortOrder, setAssignedSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load user data
  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        const userData = await userService.getUser(parseInt(userId));
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  // Get tickets where user is the requester
  const {
    tickets: requestedTickets,
    totalCount: requestedTotalCount,
    isLoading: requestedLoading
  } = useTickets({
    dateRange: dateRange,
    page: requestedCurrentPage,
    limit: ITEMS_PER_PAGE,
    sortBy: requestedSortBy,
    sortOrder: requestedSortOrder,
    status: requestedSelectedStatus,
    requestedBy: userId ? [userId] : []
  });

  // Get tickets where user is assigned
  const {
    tickets: assignedTickets,
    totalCount: assignedTotalCount,
    isLoading: assignedLoading
  } = useTickets({
    dateRange: dateRange,
    page: assignedCurrentPage,
    limit: ITEMS_PER_PAGE,
    sortBy: assignedSortBy,
    sortOrder: assignedSortOrder,
    status: assignedSelectedStatus,
    assignedTo: userId ? [userId] : []
  });

  const handleDateRangeChange = (start: string, end: string) => {
    const newDateRange = { startDate: start, endDate: end };
    setDateRange(newDateRange);
    saveDateRange(`user-tickets-${userId}-date-range`, newDateRange);
    setRequestedCurrentPage(1);
    setAssignedCurrentPage(1);
  };

  const handleViewModeChange = (mode: 'list' | 'kanban' | 'table') => {
    setViewMode(mode);
    localStorage.setItem(`userTicketsViewMode-${userId}`, mode);
  };

  const handleTicketClick = (ticketId: number) => {
    window.open(`/tickets/${ticketId}`, '_blank');
  };

  const renderTicketView = (tickets: Ticket[], loading: boolean, currentPage: number) => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (tickets.length === 0) {
      return (
        <div className={`text-center py-10 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No tickets found for the selected criteria.
        </div>
      );
    }

    switch (viewMode) {
      case 'table':
        return <TicketTable tickets={tickets} currentPage={currentPage} onTicketClick={handleTicketClick} />;
      case 'list':
        return <TicketList tickets={tickets} currentPage={currentPage} onTicketClick={handleTicketClick} />;
      case 'kanban':
        return <TicketKanbanBoard tickets={tickets} currentPage={currentPage} onTicketClick={handleTicketClick} onUpdateTicketStatus={() => {}} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-screen">
          <p className={`text-lg mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            User not found
          </p>
          <button
            onClick={() => navigate('/settings/agents')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Agents
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const currentTab = activeTab === 'requested';
  const tickets = currentTab ? requestedTickets : assignedTickets;
  const totalCount = currentTab ? requestedTotalCount : assignedTotalCount;
  const isLoading = currentTab ? requestedLoading : assignedLoading;
  const currentPage = currentTab ? requestedCurrentPage : assignedCurrentPage;
  const setCurrentPage = currentTab ? setRequestedCurrentPage : setAssignedCurrentPage;
  const selectedStatus = currentTab ? requestedSelectedStatus : assignedSelectedStatus;
  const setSelectedStatus = currentTab ? setRequestedSelectedStatus : setAssignedSelectedStatus;
  const sortBy = currentTab ? requestedSortBy : assignedSortBy;
  const setSortBy = currentTab ? setRequestedSortBy : setAssignedSortBy;
  const sortOrder = currentTab ? requestedSortOrder : assignedSortOrder;
  const setSortOrder = currentTab ? setRequestedSortOrder : setAssignedSortOrder;

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <DashboardLayout>
      <Container className={`py-6 space-y-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        {/* Page Header */}
        <div className="space-y-6">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/settings/agents')}
              className={`inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-md transition-colors ${
                isDark
                  ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Agents
            </button>

            <div className={`rounded-lg shadow-sm p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-4">
              <div
                className={`h-16 w-16 rounded-full flex items-center justify-center text-2xl font-semibold ${
                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {user.full_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {user.full_name}'s Tickets
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {user.email}
                </p>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'admin'
                      ? isDark
                        ? 'bg-purple-900 text-purple-200'
                        : 'bg-purple-100 text-purple-800'
                      : user.role === 'agent'
                      ? isDark
                        ? 'bg-blue-900 text-blue-200'
                        : 'bg-blue-100 text-blue-800'
                      : isDark
                      ? 'bg-green-900 text-green-200'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {user.role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end">
          <DateRangePicker 
            startDate={dateRange.startDate} 
            endDate={dateRange.endDate} 
            onDateChange={handleDateRangeChange} 
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`rounded-lg shadow-sm p-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-blue-500 text-white">
                  <CalendarIcon className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className={`text-sm font-medium truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Requested Tickets
                  </dt>
                  <dd className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {requestedTotalCount || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className={`rounded-lg shadow-sm p-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-green-500 text-white">
                  <CalendarIcon className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className={`text-sm font-medium truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Assigned Tickets
                  </dt>
                  <dd className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {assignedTotalCount || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('requested')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'requested'
                  ? theme === 'dark'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : theme === 'dark'
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Requested ({requestedTotalCount})
            </button>
            <button
              onClick={() => setActiveTab('assigned')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'assigned'
                  ? theme === 'dark'
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : theme === 'dark'
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Assigned ({assignedTotalCount})
            </button>
          </nav>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-shrink-0">
              {/* Status Filter */}
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                }`}
              >
                <option value="">All Statuses</option>
                {TICKET_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-shrink-0">
              {/* Sort By Filter */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                }`}
              >
                <option value="createdAt">Created Date</option>
                <option value="updatedAt">Updated Date</option>
                <option value="priority">Priority</option>
                <option value="status">Status</option>
              </select>
            </div>

            {/* Sort Order Filter */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          {/* View Toggle - Moved to the right */}
          <div className="flex-grow"></div>
          <div className="flex items-center">
            <span className={`mr-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>View:</span>
            <div className={`flex items-center rounded-lg ${theme === 'dark' ? 'bg-gray-700/70' : 'bg-gray-100'}`}>
              <button
                onClick={() => handleViewModeChange('table')}
                className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'table'
                    ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                    : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                }`}
                title="Table View"
              >
                <i className="fas fa-list mr-1"></i>
                <span className="text-xs font-medium">Table</span>
              </button>
              <button
                onClick={() => handleViewModeChange('list')}
                className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'list'
                    ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                    : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                }`}
                title="List View"
              >
                <i className="fas fa-table mr-1"></i>
                <span className="text-xs font-medium">List</span>
              </button>
              <button
                onClick={() => handleViewModeChange('kanban')}
                className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'kanban'
                    ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                    : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                }`}
                title="Kanban View"
              >
                <i className="fas fa-columns mr-1"></i>
                <span className="text-xs font-medium">Kanban</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tickets Display */}
        <div className="space-y-6">
          {renderTicketView(tickets, isLoading, currentPage)}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
        </div>
      </Container>
    </DashboardLayout>
  );
}